/**
 * WS5 #10 Stage B B5(e): the native window-global vendor loader.
 *
 * Re-homes the subset of the `angularjs.module.ts` window-global vendor loads that
 * NATIVE Angular code depends on, into a side-effect module imported FIRST by `main.ts`
 * (before app.module / angularjs.module), so the globals exist before any native
 * component ctor / field-initializer reads them AND so they survive the B6 boot-stop
 * (when angularjs.module.ts — the current loader — is deleted).
 *
 * Native consumers: the code editors read `window.js_beautify`/`window.css_beautify` (re-homed here
 * at B6 — see section 1); two image components read `window.Dropzone`.
 *
 * jQuery, bootstrap-daterangepicker and moment are all GONE. jQuery existed only for the daterange
 * plugin, which is now the in-tree FtDaterangeCalendar; moment's 38 consumers were migrated to
 * dayjs, which every file imports directly — so there is no date global to install any more, and
 * `window.moment` no longer exists. Anything reaching for it will now fail loudly rather than
 * silently binding a second date library (FTK-7475).
 *
 * (Through B5 this file only ADDED the natively-needed globals early, while the now-deleted
 * angularjs.module.ts still loaded the full vendor set during the hybrid phase. At B6 that
 * loader is gone, so every global a surviving native file reads must be loaded HERE.)
 *
 * Ace is GONE (FTK-7504). Both code editors — the contest-sheet field editors and `ft-code-editor`
 * — are one CodeMirror 6 component now, which every consumer imports directly. That removed the
 * only reason this file loaded anything eagerly (`ace.js` + `ext-language_tools.js`, 533 KB in the
 * main bundle) and the only ordering constraint it had (ace, then `ace.config.set` basePath).
 *
 * Order is still the contract for what remains (gate-blind — wrong order = runtime DOM failure, no
 * compile error). Uses `require()` (statements, top-to-bottom) rather than ESM `import` (hoisted)
 * so the order is deterministic. `export {}` makes this a module (so it doesn't redeclare the
 * global `window`); `(window as any)` avoids the global-redeclaration conflict.
 */
const w = window as any;

// (1) js-beautify → window.js_beautify / window.css_beautify (mirrors the deleted
// angularjs.module.ts:42-45). The code editors beautify the contest CSS/JS fields and the
// ft-code-editor dialogs via these globals (guarded by `typeof === 'function'`); re-homed at B6 so
// the editors keep beautifying after the AngularJS vendor loader is gone. Still the only formatter
// in the app — CodeMirror ships no beautifier of its own.
const beautify = require('js-beautify');
w.js_beautify = beautify.js_beautify;
w.css_beautify = beautify.css_beautify;

// (2) Dropzone → window.Dropzone (mirrors the deleted lazyvendor.ts:36 — missed at B6).
// ft-image-library.component.ts (wireDropzone) and ft-curation-image-replacement.component.ts
// read this global to build their file-upload boxes; both bail silently when it's absent
// (dead click/drop targets).
//
// dropzone 5.9.3's UMD min bundle sets `window.Dropzone` to the CONSTRUCTOR itself as a
// side effect, but its module export is a namespace object (`{Dropzone, default}`).
// Assigning the require() result straight onto the global therefore REPLACED the working
// constructor with a plain object: both consumers' `if (!Dropzone) return` guards still
// passed (an object is truthy) and `new Dropzone(...)` then threw "is not a constructor",
// leaving both upload boxes rendered-but-dead. Unwrap to the constructor.
const dropzoneModule = require('dropzone/dist/min/dropzone.min.js');
w.Dropzone = dropzoneModule.default ?? dropzoneModule.Dropzone ?? dropzoneModule;

export {};
